// cost-ceiling-warning.ts
//
// 06g R5 (QA CPH-2): one operator-facing line when a mission's spend reaches
// 80 % of `max_cost_usd_per_mission`, printed at most ONCE per `run` process
// (module-level memory keyed by control root — a restart warns again, which
// is the point: the operator just started a new run). Pure line builder +
// the once-guard; the caller (supervisor-dispatch-guards.ts) owns the
// cost read and the progress sink.
import type { CostAttemptLimitsConfig, MissionCostSummary } from "./mission-cost-accounting.js";
import { computeMissionCost } from "./mission-cost-accounting.js";
import { formatUsd } from "./run-progress-lines.js";

// Ratio, not a §18 threshold: the ceiling itself comes from config.
const WARN_AT_FRACTION_OF_CEILING = 0.8;

export function costCeilingWarningLine(
  cost: MissionCostSummary,
  config: CostAttemptLimitsConfig,
): string | undefined {
  const spend = cost.total_cost_usd;
  const ceiling = config.max_cost_usd_per_mission;
  if (spend === undefined || ceiling <= 0) return undefined;
  if (spend < ceiling * WARN_AT_FRACTION_OF_CEILING) return undefined;
  return `⚠ chi phí ${formatUsd(spend)} đã ≥ 80 % trần $${ceiling}`;
}

const warnedControlRoots = new Set<string>();

/** Test-only: forget which missions this process already warned about. */
export function _resetCostCeilingWarningsForTests(): void {
  warnedControlRoots.clear();
}

/** Prints the 80 % line through `progress` once per process per mission; silent without a sink. */
export async function warnCostNearCeilingOnce(
  controlRoot: string,
  config: CostAttemptLimitsConfig,
  progress: ((line: string) => void) | undefined,
): Promise<void> {
  if (progress === undefined || warnedControlRoots.has(controlRoot)) return;
  const line = costCeilingWarningLine(await computeMissionCost(controlRoot), config);
  if (line === undefined) return;
  warnedControlRoots.add(controlRoot);
  progress(line);
}
